import * as React from "react"
import { Box, Flex, HStack, Heading, Center, Spinner, Text, Input } from "@chakra-ui/react"
import { useParams } from "react-router-dom"
import { useQuery } from "react-query"
import dayjs from "dayjs"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import { TOPBAR_HEIGHT, WORLD_BBOX } from "../lib/config"
import { Sidebar } from "../components/Sidebar"
import { fetcher } from "../lib/fetcher"
import { Bbox, createBbox } from "../lib/helpers/map"
import { TripEntity } from "../lib/types/trip"
import { TripsList } from "../components/TripsList"

import { TripsBikeInfo } from "../components/TripsBikeInfo"
import TripsMap from "../components/TripsMap"
import { TripsBikeSearch } from "../components/TripsBikeSearch"
import { NoData } from "../components/NoData"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"

interface TripResults {
  count: number
  results: TripEntity[]
}

const TRIPS_PAGE_SIZE = 10
// const TOP_SECTION_HEIGHT = "210px"

export const Trips = React.memo(function _Trips() {
  useDocumentTitle("Trips")
  const { id } = useParams()
  const [selectedTrip, setSelectedTrip] = React.useState<TripEntity | undefined>()
  const [page, setPage] = React.useState<number>(1)
  const [date, setDate] = React.useState(dayjs().format())
  const [bbox, setBbox] = React.useState<Bbox | undefined>()

  const url = `trips/?bicycle=${id}&page_size=${TRIPS_PAGE_SIZE}&ended_before=${dayjs(date)
    .add(1, "d")
    .startOf("day")
    .unix()}`

  const fields = `&fields=asset_state,created,distance_m,duration,end_date,modified,route,serial_number,start_date,type,uuid`
  const getTrips = React.useCallback(
    (currentPage: number) => fetcher(url + fields + `&page=${currentPage}`),
    [url, fields],
  )

  const { isLoading, data } = useQuery<TripResults, [string, number]>([url, page], () => getTrips(page), {
    keepPreviousData: true,
  })

  React.useEffect(() => {
    if (selectedTrip && selectedTrip.route) {
      setBbox(createBbox(selectedTrip.route.coordinates))
    }
  }, [selectedTrip])

  React.useEffect(() => {
    if (
      data?.results &&
      data.results
        .filter((t) => !!t.route)
        .map((t) => t.route)
        .flat().length > 0
    ) {
      setBbox(
        createBbox(
          data.results
            .filter((t) => !!t.route)
            .map((trip) => trip.route!.coordinates)
            .flat(),
        ),
      )
    } else {
      setBbox(WORLD_BBOX)
    }
  }, [data?.results])

  const trips = data?.results?.filter((t) => !!t.route) || []

  const tripsListRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (tripsListRef.current) tripsListRef.current.scrollIntoView()
  }, [page])

  return (
    <Box>
      <Flex align="center" justify="space-between" p={6} h={TOPBAR_HEIGHT}>
        <HStack spacing={6} align="center">
          <Heading as="h3" color="gray.700">
            Trips
          </Heading>
        </HStack>

        <TripsBikeSearch />
      </Flex>
      <Box position="relative">
        <Sidebar sidebarKey="noa:trips:map" styleControl>
          {isLoading ? (
            <Center minH={400}>
              <Spinner />
            </Center>
          ) : (
            <Box h="100%" overflowY="scroll" p={6} pt={0}>
              <Box pb={4}>
                <TripsBikeInfo />
                <Flex align="center" py={4} w="100%" ref={tripsListRef}>
                  <Text fontSize="0.8rem" w="40%">
                    Trips ending before:
                  </Text>
                  <Box pos="relative" w="60%">
                    <DatePicker
                      dateFormat="MM/dd/yyyy"
                      showYearDropdown
                      popperPlacement="bottom-start"
                      showMonthDropdown
                      dropdownMode="select"
                      customInput={<Input w="100%" textAlign="center" />}
                      selected={date ? dayjs(date).toDate() : undefined}
                      onChange={(date: Date) => {
                        if (date) setDate(dayjs(date).format("YYYY-MM-DD"))
                        setPage(1)
                      }}
                    />
                  </Box>
                </Flex>
              </Box>
              <Box>
                {trips.length === 0 ? (
                  <Center h={200}>
                    <NoData>No trips</NoData>
                  </Center>
                ) : (
                  <TripsList
                    {...{
                      trips,
                      selectedTrip,
                      onSelectTrip: setSelectedTrip,
                      page,
                      pageSize: TRIPS_PAGE_SIZE,
                      setPage,
                      count: data?.count || 0,
                      isLoading,
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Sidebar>
        {bbox && <TripsMap {...{ bbox, trips, selectedTrip, onSelectTrip: setSelectedTrip }} />}
      </Box>
    </Box>
  )
})
